import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    AccountingLoad,
    AccountingOverviewMetrics,
    AccountingTabType,
    DriverPayInfo,
    Expense,
    FinanceFilterType,
    Invoice,
} from 'pages/dashboard/pages/accounting/types'
import { RootState } from 'store'

export interface AccountingState {
    // Tab state
    activeTab: AccountingTabType
    activeFinanceFilter: FinanceFilterType

    // Data
    loads: AccountingLoad[]
    invoices: Invoice[]
    expenses: Expense[]
    driverPay: DriverPayInfo[]
    overviewMetrics: AccountingOverviewMetrics | null

    // Counts
    counts: {
        upcoming: number
        inTransit: number
        history: number
    }

    // Pagination
    page: number
    rowsPerPage: number
    total: number

    // Filters
    searchQuery: string
    brokerFilter: string[]
    driverFilter: string[]
    dateRange: { start: string | null; end: string | null }

    // UI state
    loading: boolean
    error: string | null

    // Modals
    createInvoiceModalOpen: boolean
    factorModalOpen: boolean
}

const initialState: AccountingState = {
    // Tab state
    activeTab: 'history',
    activeFinanceFilter: 'all',

    // Data
    loads: [],
    invoices: [],
    expenses: [],
    driverPay: [],
    overviewMetrics: null,

    // Counts
    counts: {
        upcoming: 0,
        inTransit: 0,
        history: 0,
    },

    // Pagination
    page: 1,
    rowsPerPage: 25,
    total: 0,

    // Filters
    searchQuery: '',
    brokerFilter: [],
    driverFilter: [],
    dateRange: { start: null, end: null },

    // UI state
    loading: false,
    error: null,

    // Modals
    createInvoiceModalOpen: false,
    factorModalOpen: false,
}

const accountingSlice = createSlice({
    name: 'accounting',
    initialState,
    reducers: {
        // Tab actions
        setActiveTab: (state, action: PayloadAction<AccountingTabType>) => {
            state.activeTab = action.payload
            state.page = 1
        },
        setActiveFinanceFilter: (state, action: PayloadAction<FinanceFilterType>) => {
            state.activeFinanceFilter = action.payload
            state.page = 1
        },

        // Data actions
        setLoads: (state, action: PayloadAction<AccountingLoad[]>) => {
            state.loads = action.payload
        },
        setInvoices: (state, action: PayloadAction<Invoice[]>) => {
            state.invoices = action.payload
        },
        setExpenses: (state, action: PayloadAction<Expense[]>) => {
            state.expenses = action.payload
        },
        setDriverPay: (state, action: PayloadAction<DriverPayInfo[]>) => {
            state.driverPay = action.payload
        },
        setOverviewMetrics: (state, action: PayloadAction<AccountingOverviewMetrics>) => {
            state.overviewMetrics = action.payload
        },

        // Counts
        setCounts: (state, action: PayloadAction<{ upcoming: number; inTransit: number; history: number }>) => {
            state.counts = action.payload
        },

        // Pagination actions
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload
        },
        setRowsPerPage: (state, action: PayloadAction<number>) => {
            state.rowsPerPage = action.payload
            state.page = 1
        },
        setTotal: (state, action: PayloadAction<number>) => {
            state.total = action.payload
        },

        // Filter actions
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload
            state.page = 1
        },
        setBrokerFilter: (state, action: PayloadAction<string[]>) => {
            state.brokerFilter = action.payload
            state.page = 1
        },
        setDriverFilter: (state, action: PayloadAction<string[]>) => {
            state.driverFilter = action.payload
            state.page = 1
        },
        setDateRange: (state, action: PayloadAction<{ start: string | null; end: string | null }>) => {
            state.dateRange = action.payload
            state.page = 1
        },
        clearFilters: (state) => {
            state.searchQuery = ''
            state.brokerFilter = []
            state.driverFilter = []
            state.dateRange = { start: null, end: null }
            state.activeFinanceFilter = 'all'
            state.page = 1
        },

        // UI state actions
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },

        // Modal actions
        openCreateInvoiceModal: (state) => {
            state.createInvoiceModalOpen = true
        },
        closeCreateInvoiceModal: (state) => {
            state.createInvoiceModalOpen = false
        },
        openFactorModal: (state) => {
            state.factorModalOpen = true
        },
        closeFactorModal: (state) => {
            state.factorModalOpen = false
        },

        // Reset state
        resetAccountingState: () => initialState,
    },
})

export const {
    setActiveTab,
    setActiveFinanceFilter,
    setLoads,
    setInvoices,
    setExpenses,
    setDriverPay,
    setOverviewMetrics,
    setCounts,
    setPage,
    setRowsPerPage,
    setTotal,
    setSearchQuery,
    setBrokerFilter,
    setDriverFilter,
    setDateRange,
    clearFilters,
    setLoading,
    setError,
    openCreateInvoiceModal,
    closeCreateInvoiceModal,
    openFactorModal,
    closeFactorModal,
    resetAccountingState,
} = accountingSlice.actions

// Selectors
export const selectAccountingState = (state: RootState) => state.accounting
export const selectActiveTab = (state: RootState) => state.accounting.activeTab
export const selectActiveFinanceFilter = (state: RootState) => state.accounting.activeFinanceFilter
export const selectLoads = (state: RootState) => state.accounting.loads
export const selectInvoices = (state: RootState) => state.accounting.invoices
export const selectExpenses = (state: RootState) => state.accounting.expenses
export const selectDriverPay = (state: RootState) => state.accounting.driverPay
export const selectOverviewMetrics = (state: RootState) => state.accounting.overviewMetrics
export const selectCounts = (state: RootState) => state.accounting.counts
export const selectPage = (state: RootState) => state.accounting.page
export const selectRowsPerPage = (state: RootState) => state.accounting.rowsPerPage
export const selectTotal = (state: RootState) => state.accounting.total
export const selectSearchQuery = (state: RootState) => state.accounting.searchQuery
export const selectBrokerFilter = (state: RootState) => state.accounting.brokerFilter
export const selectDriverFilter = (state: RootState) => state.accounting.driverFilter
export const selectDateRange = (state: RootState) => state.accounting.dateRange
export const selectLoading = (state: RootState) => state.accounting.loading
export const selectError = (state: RootState) => state.accounting.error
export const selectCreateInvoiceModalOpen = (state: RootState) => state.accounting.createInvoiceModalOpen
export const selectFactorModalOpen = (state: RootState) => state.accounting.factorModalOpen

export default accountingSlice.reducer
